import { registerModule } from './registry'
import {
  ExternalLinkFieldModule,
  TextareaFieldModule,
  TextFieldModule,
} from './primitives/PrimitiveModules'
import {
  AssetsModule,
  ExternalLinkPanelModule,
  FeScheduleModule,
  FeScheduleReferenceModule,
  IframeEmbedModule,
  PartsModule,
  PartProcurementModule,
  StoreModule,
  WorkPermitModule,
} from './core/CoreModules'
import { DocumentCollectionModule, validateDocumentCollection } from '../DocumentCollectionModule'
import { FeVisitTrackingModule } from './core/FeVisitTrackingModule'
import type { TaskFieldContext, ValidationResult } from './types'

function requiredPrimitiveValidate(ctx: TaskFieldContext): ValidationResult {
  if (!ctx.field.required) return { valid: true }
  const v = ctx.formData[ctx.field.key]
  if (v === null || v === undefined || String(v).trim() === '') {
    return { valid: false, message: `"${ctx.field.label}" is required` }
  }
  return { valid: true }
}
export function registerTaskFieldModules(): void {
  registerModule({
    moduleKey: 'primitive.text',
    label: 'Text',
    category: 'forms',
    scope: 'task',
    component: TextFieldModule,
    validate: requiredPrimitiveValidate,
  })
  registerModule({
    moduleKey: 'primitive.textarea',
    label: 'Text area',
    category: 'forms',
    scope: 'task',
    component: TextareaFieldModule,
    validate: requiredPrimitiveValidate,
  })
  registerModule({
    moduleKey: 'primitive.external_link',
    label: 'External link',
    category: 'forms',
    scope: 'task',
    badge: 'Link',
    component: ExternalLinkFieldModule,
    validate: requiredPrimitiveValidate,
  })
  registerModule({
    moduleKey: 'core.assignee',
    label: 'Assignee',
    category: 'operations',
    scope: 'task',
    badge: 'Assignee',
    component: TextFieldModule,
  })
  registerModule({
    moduleKey: 'core.store',
    label: 'Store',
    category: 'operations',
    scope: 'ticket',
    badge: 'Store',
    component: StoreModule,
    validate: (ctx) => {
      if (ctx.field.required && ctx.storeId == null) {
        return { valid: false, message: 'Link a store before completing this task.' }
      }
      return { valid: true }
    },
  })
  registerModule({
    moduleKey: 'core.fe_schedule',
    label: 'FE schedule',
    category: 'operations',
    scope: 'ticket',
    badge: 'Store · FE',
    component: FeScheduleModule,
    isBlocked: (ctx) => {
      if (ctx.storeId == null) {
        return { blocked: true, message: 'Link a store before scheduling field engineers.' }
      }
      return { blocked: false }
    },
  })
  registerModule({
    moduleKey: 'core.fe_schedule_ref',
    label: 'FE schedule (read-only)',
    category: 'operations',
    scope: 'ticket',
    badge: 'FE',
    component: FeScheduleReferenceModule,
  })
  registerModule({
    moduleKey: 'core.assets',
    label: 'Assets',
    category: 'operations',
    scope: 'ticket',
    badge: 'Assets',
    component: AssetsModule,
  })
  registerModule({
    moduleKey: 'core.parts',
    label: 'Parts',
    category: 'operations',
    scope: 'ticket',
    badge: 'Parts',
    component: PartsModule,
  })
  registerModule({
    moduleKey: 'core.part_procurement',
    label: 'Part procurement',
    category: 'operations',
    scope: 'task',
    badge: 'Part',
    component: PartProcurementModule,
    // Procurement state lives on linked ticket-part records, not in form_data.
    // Completion eligibility is enforced from those records by the task drawer.
    validate: () => ({ valid: true }),
  })
  registerModule({
    moduleKey: 'core.document_collection',
    label: 'Document collection',
    category: 'operations',
    scope: 'task',
    badge: 'Documents',
    component: DocumentCollectionModule,
    validate: (ctx) => validateDocumentCollection(ctx),
  })
  registerModule({
    moduleKey: 'core.fe_visit_tracking',
    label: 'FE visit tracking',
    category: 'operations',
    scope: 'task',
    badge: 'FE Visit',
    component: FeVisitTrackingModule,
  })
  registerModule({
    moduleKey: 'core.work_permit',
    label: 'Work permit',
    category: 'operations',
    scope: 'ticket',
    badge: 'Permit',
    component: WorkPermitModule,
  })
  registerModule({
    moduleKey: 'embed.iframe',
    label: 'Embed (iframe)',
    category: 'embeds',
    scope: 'task',
    badge: 'Embed',
    component: IframeEmbedModule,
  })
  registerModule({
    moduleKey: 'embed.external_link_panel',
    label: 'External portal',
    category: 'embeds',
    scope: 'task',
    badge: 'External',
    component: ExternalLinkPanelModule,
  })
}

// Register on import
registerTaskFieldModules()
